import { ColumnType } from "antd/es/table";
import { useDispatch } from "react-redux";
import React, { useEffect, useState, useMemo } from "react";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import reportApis from "../../api/reports";
import { TooltipComponent } from "../../components/Component";
import { Drawer, Table, Progress, Tooltip } from 'antd';
import { tasksApis } from "../../api/NewTasks";

interface TaskItem {
  taskId: string;
  taskName: string;
  taskOwnerName: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityName: string;
  completionPercentage: number;
  taskStatus: string;
  uid: string;
}

const ChecklistAdherenceByUserReport = () => {
  const [userIdFilterData, setUserIdFilterData] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [tasksData, setTasksData] = useState<TaskItem[]>([]);
  const [selectedUser, setSelectedUser] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const [drawerPagination, setDrawerPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const { id: projectId, projectName } = location.state || {};

  const columns: ColumnType<any>[] = [
    {
      title: "User Name",
      dataIndex: "assigedTo",
      key: "assigedTo",
      width: 180,
      fixed: "left",
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
    },
    {
      title: "Total Tasks",
      dataIndex: "totalTasks",
      key: "totalTasks",
      width: 100,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
    },
    {
      title: "Checklist Required",
      dataIndex: "checklistsRequired",
      key: "checklistsRequired",
      width: 100,
      render(text: any, record: any) {
        return (
          <>
            <div
              className="inlineTableCellLink w-100"
              id={`task-${record?.taskId}`}
              onClick={() => handleChecklistRequiredClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`task-${record?.taskId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Checklist Completed",
      dataIndex: "checklistsCompleted",
      key: "checklistsCompleted",
      width: 100,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
    },
    {
      title: "Adherence Percentage",
      dataIndex: "adherencePercentage",
      key: "adherencePercentage",
      width: 80,
      render: (text, record) => {
        if (text === null || text === undefined || text === "") return "N/A";
        
        const percentage = parseFloat(text.toString().replace('%', ''));
        
        return (
          <Tooltip
            title={`Completed: ${record.checklistsCompleted}, Required: ${record.checklistsRequired}`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      }
    },
  ];

  const fetchUserDropdown = async () => {
    try {
      const response = await tasksApis.getOwnersFilterData(
        projectId?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setUserIdFilterData(options);
      }
    } catch (error) {
      console.error("eroor");
    }
  };

  const handleChecklistRequiredClick = async (record: any) => {
    setSelectedUser({
      userId: record.userId,
      userName: record.assigedTo
    });
    setLoading(true);
    setDrawerVisible(true);

    // Reset pagination when opening drawer
    setDrawerPagination(prev => ({
      ...prev,
      current: 1,
      total: 0
    }));

    try {
      const response = await reportApis.fetchPMSReports({
        page: 1,
        pageSize: drawerPagination.pageSize,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "DESC",
        direction: "P",
        reportName: "countListForChecklistAdherenceByUser",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
        setDrawerPagination(prev => ({
          ...prev,
          total: data?.totalCount || 0
        }));
      }
    } catch (error) {
      console.error("Error fetching Checklist Required tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrawerTableChange = async (newPagination: any) => {
    if (!selectedUser) return;

    setLoading(true);

    try {
      const response = await reportApis.fetchPMSReports({
        page: newPagination.current,
        pageSize: newPagination.pageSize,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "DESC",
        direction: "P",
        reportName: "countListForChecklistAdherenceByUser",
        taskOwnerIds: selectedUser.userId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
        setDrawerPagination(prev => ({
          ...prev,
          current: newPagination.current,
          pageSize: newPagination.pageSize,
          total: data?.totalCount || 0
        }));
      }
    } catch (error) {
      console.error("Error fetching paginated tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDropdown();
    dispatch(
      updateMenuData([
        {
          text: "Checklist Adherence by User Report",
          link: "pms/checklist-adherence-by-user-report",
        },
      ])
    );
  }, []);

  // Debug effect to log selectedUser changes
  useEffect(() => {
    console.log("selectedUser changed:", selectedUser);
    if (selectedUser?.userId) {
      console.log("taskOwnerId will be:", Number(selectedUser.userId));
    }
  }, [selectedUser]);

  const drawerColumns: ColumnType<any>[] = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        width: 200,
        fixed: "left",
        render(text: any, record: any) {
          return (
            <>
              <span
                className="inlineTableCellLink"
                id={`Task-${record?.taskId}`}
                onClick={() => {
                  navigate(`/pms/project/task-detail/${record.taskId}`, {
                    state: {
                      projectId,
                      projectName,
                      returnPath: location.pathname,
                      returnState: location.state,
                    },
                  });
                }}
              >
                {text}
              </span>
            </>
          );
        },
      },
      {
        title: "Owner Name",
        dataIndex: "assignedTo",
        key: "assignedTo",
        width: 200,
        render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
      },
      {
        title: "Checklist Name",
        dataIndex: "checklistName",
        key: "checklistName",
        width: 200,
        render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
      },
      {
        title: "Checklist Status",
        dataIndex: "checklistCompletedStatus",
        key: "checklistCompletedStatus",
        width: 100,
       render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
      },
      {
        title: "Status",
        dataIndex: "taskStatus",
        key: "taskStatus",
        width: 150,
        fixed: "right",
      },
    ],
    [dispatch]
  );

  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="Checklist Adherence by User Report"
        pageDescription=""
        previousPath={{
          link: "/pms/project/reports",
          state: location.state,
        }}
        filters={{
          defaultFilters: {
            dateRange: {
              title: "Custom Date Range",
            },
          },
          customFilters: [
            {
              type: "multiple_selection",
              name: "UserId",
              label: "Users",
              options: userIdFilterData,
            },
          ],
        }}
      />
      <Drawer
        title={`Tasks for ${selectedUser?.userName || "User"}`}
        width={1200}
        zIndex={100}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Table
          columns={drawerColumns}
          dataSource={tasksData}
          loading={loading}
          scroll={{ x: 1500 }}
          rowKey="taskId"
          pagination={drawerPagination}
          onChange={handleDrawerTableChange}
        />
      </Drawer>
    </>
  );
};

export default ChecklistAdherenceByUserReport;

